import { useCallback, useEffect, useMemo, useState } from "react";
import intl from "react-intl-universal";
import { TbAdjustments, TbMoon, TbRefresh, TbSun } from "react-icons/tb";
import { Link, useLocation } from "react-router-dom";
import { useBaasInstance } from "../../../../../Contexts/BaasInstanceContext";
import { useBaasCapabilities } from "../../../../../Hooks/useBaasCapabilities";
import { useToast } from "../../../../../Hooks/toast";
import {
  getPixAvailableLimits,
  getPixLimits,
} from "../../../../../Services/baasPixApi";
import { fetchBaasStablesAccountLimits } from "../../../../../Services/baasStablesWalletApi";
import { formatCurrency, formatMonetaryValue } from "../../../../../Utils/formatters";
import { BaasLimitUsageCard } from "../../components/BaasLimitUsageCard";
import {
  PIX_LIMIT_PERIODS,
  resolvePixLimitMeta,
} from "../../../Pix/PixLimits/constants";
import { BAAS_STABLES_LIMIT_POLICY_TEMPLATES } from "../../Stables/baasStablesLimitsConstants";
import {
  buildBaasAccountLimitesConfigPath,
  getVisibleBaasLimitRailTabs,
} from "../../constants";

const BORDER_CARD =
  "rounded-xl border border-gray-200 bg-white dark:border-brand-dark-border dark:bg-brand-dark-surface";

const fromMajor = (reais) => {
  const n = Number(reais);
  if (!Number.isFinite(n)) return "—";
  return formatMonetaryValue(n, "BRL");
};

const formatPeriodHours = (hourStart, hourEnd) => {
  const start = Number(hourStart);
  const end = Number(hourEnd);
  if (!Number.isFinite(start) || !Number.isFinite(end)) return null;
  const displayEnd = (end + 1) % 24;
  return `${start}h às ${displayEnd}h`;
};

/** BaaS period 1 = diurno, 2 = noturno; also accepts mapped string keys. */
const resolvePeriodGroup = (item) => {
  const period = item?.period;
  if (period === "noturno" || period === 2 || period === "2") return "noturno";
  if (period === "diurno" || period === 1 || period === "1") return "diurno";
  const start = Number(item?.period_hour_start);
  const end = Number(item?.period_hour_end);
  if (Number.isFinite(start) && Number.isFinite(end) && start > end) {
    return "noturno";
  }
  return "diurno";
};

const periodSectionLabel = (groupId) =>
  groupId === "noturno"
    ? intl.get("baas.accounts.detail.limites.periodNight")
    : intl.get("baas.accounts.detail.limites.periodDay");

/** Major BRL from available payload (`used`/`limit`) or IB cents shape. */
const resolveUsageMajors = (item) => {
  const usedMajor = Number(item.used);
  const limitMajor = Number(item.limit);
  if (Number.isFinite(usedMajor) || Number.isFinite(limitMajor)) {
    return {
      used: Number.isFinite(usedMajor) ? usedMajor : 0,
      max: Number.isFinite(limitMajor) ? limitMajor : 0,
    };
  }

  const usedCents = Number(item.used_cents ?? item.usedCents);
  const totalCents = Number(item.total_cents ?? item.totalCents);
  return {
    used: Number.isFinite(usedCents) ? usedCents / 100 : 0,
    max: Number.isFinite(totalCents) ? totalCents / 100 : 0,
  };
};

const PixLimitCard = ({ item }) => {
  const { used, max } = resolveUsageMajors(item);
  const hours =
    item.period_hours ||
    formatPeriodHours(item.period_hour_start, item.period_hour_end);
  const meta = resolvePixLimitMeta(item);
  const extraBits = [
    item.monthly_limit != null
      ? `${intl.get("baas.pix.limits.monthlyLabel")}: ${fromMajor(item.monthly_used || 0)} / ${fromMajor(item.monthly_limit)}`
      : null,
    item.per_operation_limit != null
      ? `${intl.get("baas.pix.limits.perOpLabel")}: ${fromMajor(item.per_operation_limit)}`
      : null,
  ].filter(Boolean);

  return (
    <BaasLimitUsageCard
      label={meta.label}
      description={[meta.description, hours].filter(Boolean).join(" · ")}
      used={used}
      max={max}
      formatValue={fromMajor}
      comingSoon={Boolean(meta.comingSoon)}
      extra={
        extraBits.length ? (
          <p className="mt-2 text-[11px] text-gray-500 dark:text-gray-400">
            {extraBits.join(" · ")}
          </p>
        ) : null
      }
    />
  );
};

const PeriodSection = ({ groupId, items }) => {
  if (!items.length) return null;
  const Icon = groupId === "noturno" ? TbMoon : TbSun;
  const sample = items[0];
  const hours =
    sample?.period_hours ||
    formatPeriodHours(sample?.period_hour_start, sample?.period_hour_end) ||
    PIX_LIMIT_PERIODS[groupId]?.hours;

  return (
    <section className="space-y-3">
      <div className="flex flex-wrap items-center gap-2">
        <Icon className="h-4 w-4 text-[#e7a73f]" aria-hidden />
        <h3 className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {periodSectionLabel(groupId)}
        </h3>
        {hours ? (
          <span className="text-xs text-gray-500 dark:text-gray-400">
            ({hours})
          </span>
        ) : null}
      </div>
      <div className="grid gap-3 md:grid-cols-2">
        {items.map((item, index) => (
          <PixLimitCard
            key={`${item.type}-${item.period}-${item.period_hour_start}-${index}`}
            item={item}
          />
        ))}
      </div>
    </section>
  );
};

const LimitsComingSoon = () => (
  <div className={`${BORDER_CARD} px-4 py-4 sm:px-5`}>
    <p className="text-sm font-semibold text-brand-dark dark:text-gray-100">
      {intl.get("baas.accounts.detail.limites.comingSoonTitle")}
    </p>
    <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
      {intl.get("baas.accounts.detail.limites.comingSoonBody")}
    </p>
  </div>
);

const RailSection = ({ id, title, hint, actions, children }) => (
  <section id={id} className="scroll-mt-4 space-y-3">
    <div className="min-w-0 space-y-0.5">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="text-sm font-semibold text-brand-dark dark:text-gray-100">
          {title}
        </h3>
        {actions ? (
          <div className="flex flex-wrap items-center gap-2">{actions}</div>
        ) : null}
      </div>
      {hint ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">{hint}</p>
      ) : null}
    </div>
    {children}
  </section>
);

const groupByPeriod = (items) => {
  const groups = { diurno: [], noturno: [] };
  for (const item of items || []) {
    groups[resolvePeriodGroup(item)].push(item);
  }
  return groups;
};

export const BaasAccountLimitsPanel = ({ accountId }) => {
  const { addToast } = useToast();
  const { selectedInstance } = useBaasInstance();
  const { can } = useBaasCapabilities();
  const location = useLocation();
  const [pixLoading, setPixLoading] = useState(false);
  const [stablesLoading, setStablesLoading] = useState(false);
  const [limits, setLimits] = useState(null);
  const [available, setAvailable] = useState([]);
  const [stablesLimits, setStablesLimits] = useState(null);

  const canViewPix = can("processing.pix.view");
  const canViewStables = can("processing.stables.view");
  const canConfigureLimits = can("settings.write") || can("processing.pix.write");
  const visibleRailTabs = useMemo(
    () => getVisibleBaasLimitRailTabs({ canViewPix, canViewStables }),
    [canViewPix, canViewStables],
  );
  const comingSoonRails = useMemo(
    () => visibleRailTabs.filter((tab) => tab.id === "swift" || tab.id === "transfers"),
    [visibleRailTabs],
  );

  const refreshPix = useCallback(async () => {
    if (!selectedInstance || !accountId || !canViewPix) {
      setLimits(null);
      setAvailable([]);
      return;
    }
    setPixLoading(true);
    try {
      const [next, nextAvailable] = await Promise.all([
        getPixLimits(selectedInstance, accountId, { resolved: true }),
        getPixAvailableLimits(selectedInstance, accountId),
      ]);
      setLimits(next);
      setAvailable(nextAvailable);
    } catch (error) {
      setLimits(null);
      setAvailable([]);
      addToast({
        type: "error",
        title: error?.message || intl.get("baas.pix.error.load"),
      });
    } finally {
      setPixLoading(false);
    }
  }, [selectedInstance, accountId, addToast, canViewPix]);

  const refreshStables = useCallback(async () => {
    if (!selectedInstance || !accountId || !canViewStables) {
      setStablesLimits(null);
      return;
    }
    setStablesLoading(true);
    try {
      const next = await fetchBaasStablesAccountLimits(selectedInstance, accountId);
      setStablesLimits(next);
    } catch (error) {
      setStablesLimits(null);
      addToast({
        type: "error",
        title: error?.message || intl.get("baas.stablesWallet.loadFailed"),
      });
    } finally {
      setStablesLoading(false);
    }
  }, [selectedInstance, accountId, addToast, canViewStables]);

  useEffect(() => {
    refreshPix();
  }, [refreshPix]);

  useEffect(() => {
    refreshStables();
  }, [refreshStables]);

  useEffect(() => {
    const rail = new URLSearchParams(location.search).get("rail");
    if (!rail) return;
    const node = document.getElementById(`baas-limits-${rail}`);
    if (!node) return;
    node.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [location.search, canViewPix, canViewStables]);

  const availableByPeriod = useMemo(
    () => groupByPeriod(available),
    [available],
  );

  const stablesRows = useMemo(() => {
    const itemsById = Object.fromEntries(
      (stablesLimits?.items || []).map((item) => [item.id, item]),
    );
    const seen = new Set();
    return BAAS_STABLES_LIMIT_POLICY_TEMPLATES.filter((template) => {
      if (seen.has(template.limitId)) return false;
      seen.add(template.limitId);
      return true;
    }).map((template) => {
      const item = itemsById[template.limitId];
      return {
        key: template.limitId,
        label: intl.get(template.labelKey),
        description: intl.get(template.descriptionKey),
        used: Number(item?.used ?? 0),
        max: Number(item?.max ?? 0),
        usage: Array.isArray(item?.usage) ? item.usage : [],
      };
    });
  }, [stablesLimits]);

  const renderPixTab = () => {
    if (!canViewPix) {
      return (
        <div
          className={`${BORDER_CARD} px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400`}
        >
          {intl.get("baas.home.capabilities.permissionDenied")}
        </div>
      );
    }

    const policyItems = limits?.items || [];
    const usageDiurno = availableByPeriod.diurno.length
      ? availableByPeriod.diurno
      : policyItems.filter((item) => resolvePeriodGroup(item) === "diurno").map((item) => ({
          ...item,
          used: 0,
          limit: Number(item.amount) || 0,
        }));
    const usageNoturno = availableByPeriod.noturno.length
      ? availableByPeriod.noturno
      : policyItems.filter((item) => resolvePeriodGroup(item) === "noturno").map((item) => ({
          ...item,
          used: 0,
          limit: Number(item.amount) || 0,
        }));
    const hasUsage = usageDiurno.length > 0 || usageNoturno.length > 0;

    if (pixLoading) {
      return (
        <div className={`${BORDER_CARD} px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400`}>
          {intl.get("baas.pix.limits.loading")}
        </div>
      );
    }

    return (
      <div className="space-y-6">
        {!hasUsage ? (
          <p className={`${BORDER_CARD} px-4 py-6 text-sm text-gray-500 dark:text-gray-400`}>
            {intl.get("baas.accounts.detail.limites.empty")}
          </p>
        ) : (
          ["diurno", "noturno"].map((groupId) => {
            const items = groupId === "diurno" ? usageDiurno : usageNoturno;
            if (!items.length) return null;
            return <PeriodSection key={groupId} groupId={groupId} items={items} />;
          })
        )}
      </div>
    );
  };

  const renderStablesTab = () => {
    if (!canViewStables) {
      return (
        <div
          className={`${BORDER_CARD} px-6 py-10 text-center text-sm text-gray-500 dark:text-gray-400`}
        >
          {intl.get("baas.home.capabilities.permissionDenied")}
        </div>
      );
    }

    const hasAny = stablesRows.some((row) => row.max > 0 || row.used > 0);

    return stablesLoading ? (
      <p className={`${BORDER_CARD} px-4 py-6 text-center text-sm text-gray-500 dark:text-gray-400`}>
        {intl.get("baas.pix.limits.loading")}
      </p>
    ) : !hasAny ? (
      <p className={`${BORDER_CARD} px-4 py-6 text-sm text-gray-500 dark:text-gray-400`}>
        {intl.get("baas.stablesWallet.limits.empty")}
      </p>
    ) : (
      <div className="grid gap-3 md:grid-cols-2">
        {stablesRows.map((row) => (
          <BaasLimitUsageCard
            key={row.key}
            label={row.label}
            description={row.description}
            used={row.used}
            max={row.max}
            usage={row.usage}
            formatValue={formatCurrency}
          />
        ))}
      </div>
    );
  };

  const railActionClass =
    "inline-flex items-center gap-1 text-xs font-medium text-gray-500 transition hover:text-brand-dark disabled:cursor-not-allowed disabled:opacity-50 dark:text-gray-400 dark:hover:text-gray-200";

  const railActions = (railId, { onRefresh, refreshing }) => (
    <>
      {canConfigureLimits ? (
        <Link
          to={buildBaasAccountLimitesConfigPath(accountId, railId)}
          className={`${railActionClass} link-no-zoom`}
        >
          <TbAdjustments className="h-3.5 w-3.5" aria-hidden />
          {intl.get("baas.accounts.detail.limites.configureRail", {
            rail: intl.get(`baas.accounts.detail.limites.tabs.${railId}`),
          })}
        </Link>
      ) : null}
      {onRefresh ? (
        <button
          type="button"
          className={railActionClass}
          onClick={onRefresh}
          disabled={refreshing}
        >
          <TbRefresh className="h-3.5 w-3.5" aria-hidden />
          {intl.get("baas.pix.refresh")}
        </button>
      ) : null}
    </>
  );

  return (
    <div className="space-y-8">
      <header>
        <p className="dashboard-section-label mb-0">
          {intl.get("baas.accounts.detail.tabs.limites")}
        </p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
          {intl.get("baas.accounts.detail.limites.subtitle")}
        </p>
      </header>

      {canViewPix ? (
        <RailSection
          id="baas-limits-pix"
          title={intl.get("baas.accounts.detail.limites.tabs.pix")}
          hint={intl.get("baas.pix.limits.hint")}
          actions={railActions("pix", { onRefresh: refreshPix, refreshing: pixLoading })}
        >
          {renderPixTab()}
        </RailSection>
      ) : null}

      {canViewStables ? (
        <RailSection
          id="baas-limits-stables"
          title={intl.get("baas.accounts.detail.limites.tabs.stables")}
          hint={intl.get("baas.stablesWallet.limits.subtitle")}
          actions={railActions("stables", {
            onRefresh: refreshStables,
            refreshing: stablesLoading,
          })}
        >
          {renderStablesTab()}
        </RailSection>
      ) : null}

      {comingSoonRails.map((tab) => (
        <RailSection
          key={tab.id}
          id={`baas-limits-${tab.id}`}
          title={intl.get(tab.labelKey)}
          actions={
            canConfigureLimits ? (
              <Link
                to={buildBaasAccountLimitesConfigPath(accountId, tab.id)}
                className={`${railActionClass} link-no-zoom`}
              >
                <TbAdjustments className="h-3.5 w-3.5" aria-hidden />
                {intl.get("baas.accounts.detail.limites.configureRail", {
                  rail: intl.get(tab.labelKey),
                })}
              </Link>
            ) : null
          }
        >
          <LimitsComingSoon />
        </RailSection>
      ))}
    </div>
  );
};

export default BaasAccountLimitsPanel;
